// TAX-EMAIL-RECEIVED-001 — jurisdiction-scoped lifecycle rollup for Tax Collector email requests (Fax parity).
// Mirrors backend tax_collector_email_service.jurisdiction_lifecycle / aggregate_status.
const ACTIONABLE = ['sending', 'sent', 'received'];

const jurKey = (r) => String(r.jurisdiction_id || '')
  || `${String(r.jurisdiction_name || '').trim().toUpperCase()}|${String(r.jurisdiction_type || '').trim().toLowerCase()}`;

/** TAX-EMAIL-MULTIUPLOAD-001 — every response document of a request: multi-file `received_file_ids` ∪ legacy `received_file_id`. */
export const responseFileIds = (r) => {
  const ids = (Array.isArray(r?.received_file_ids) ? r.received_file_ids : []).filter(Boolean);
  if (r?.received_file_id && !ids.includes(r.received_file_id)) ids.unshift(r.received_file_id);
  return ids;
};

/** Display rows for the response documents (falls back to ids when legacy records carry no metadata). */
export const responseFiles = (r) => {
  const meta = Array.isArray(r?.received_files) ? r.received_files : [];
  return responseFileIds(r).map((id) => meta.find((m) => m.id === id) || { id, filename: 'Response document' });
};

export const jurisdictionLifecycle = (requests) => {
  const groups = {};
  (Array.isArray(requests) ? requests : []).forEach((r) => {
    if (!ACTIONABLE.includes(r.status)) return;
    const g = groups[jurKey(r)] || (groups[jurKey(r)] = { jurisdiction_name: r.jurisdiction_name, lastSent: '', lastReceived: '', receivedFileIds: [] });
    if (r.status === 'received') {
      const ts = r.received_at || r.created_at || '';
      if (ts > g.lastReceived) g.lastReceived = ts;
      responseFileIds(r).forEach((id) => { if (!g.receivedFileIds.includes(id)) g.receivedFileIds.push(id); });
    } else if ((r.created_at || '') > g.lastSent) {
      g.lastSent = r.created_at || '';
    }
  });
  return Object.values(groups).map((g) => ({ ...g, received: !!g.lastReceived && g.lastSent <= g.lastReceived }));
};

/** A request is "superseded" when a newer send for the same jurisdiction exists — its Received action moves to the newest. */
export const isLatestRequest = (rec, requests) =>
  !(Array.isArray(requests) ? requests : []).some((o) => o.id !== rec.id && ACTIONABLE.includes(o.status)
    && jurKey(o) === jurKey(rec) && (o.created_at || '') > (rec.created_at || ''));

/**
 * TAX-EMAIL-UI-SIMPLIFY-001 — presentation grouping only. Per jurisdiction the CURRENT record is the newest
 * actionable one (sending/sent/received); if a jurisdiction has none, its newest record of any status (so a
 * failure still surfaces with Retry). Everything else is read-only history. Nothing is dropped.
 */
export const splitCurrentAndHistory = (requests) => {
  const byJur = {};
  (Array.isArray(requests) ? requests : []).forEach((r) => { (byJur[jurKey(r)] = byJur[jurKey(r)] || []).push(r); });
  const currentIds = new Set();
  Object.values(byJur).forEach((list) => {
    const sorted = [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    const pick = sorted.find((r) => ACTIONABLE.includes(r.status)) || sorted[0];
    if (pick) currentIds.add(pick.id);
    // a failure NEWER than the current actionable record (e.g. failed follow-up) must stay visible with Retry
    if (sorted[0] && sorted[0] !== pick && !ACTIONABLE.includes(sorted[0].status)) currentIds.add(sorted[0].id);
  });
  const all = [...(Array.isArray(requests) ? requests : [])].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  return { current: all.filter((r) => currentIds.has(r.id)), history: all.filter((r) => !currentIds.has(r.id)) };
};

/**
 * {state: null|'sent'|'partial'|'received'|'reviewed', total, received, outstanding[]}
 * - 'received' (blue) only when EVERY emailed jurisdiction has responded
 * - 'partial' (gray + count tooltip) when some responded — outstanding jurisdictions are never hidden
 * - 'reviewed' (green) from order-level tax_email_status once the processor opened every response file
 */
export const aggregateTaxEmailStatus = (taxEmailStatus, requests) => {
  const rows = jurisdictionLifecycle(requests);
  if (!rows.length) return { state: taxEmailStatus || null, total: 0, received: 0, outstanding: [] };
  const outstanding = rows.filter((g) => !g.received).map((g) => g.jurisdiction_name);
  const received = rows.length - outstanding.length;
  let state = 'sent';
  if (!outstanding.length) state = taxEmailStatus === 'reviewed' ? 'reviewed' : 'received';
  else if (received > 0) state = 'partial';
  return { state, total: rows.length, received, outstanding };
};

export const taxEmailTooltip = (agg) => {
  switch (agg.state) {
    case 'sent': return 'Email sent — awaiting response';
    case 'partial': return `${agg.received} of ${agg.total} email responses received — awaiting ${agg.outstanding.join(', ')}`;
    case 'received': return 'All email responses received — awaiting processor review';
    case 'reviewed': return 'Email responses reviewed by processor';
    default: return '';
  }
};
